// src/dashboard/ClientProfile.js
import { useState } from 'react';
import { User, MessageSquare, BarChart, Play, Mic } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const ClientProfile = ({ profile, user, refreshProfile }) => {
	const navigate = useNavigate();
	
	// Format date string
	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	// Check if user has completed any Natural Matchmaker sessions
	const hasSessionData = profile && profile.responses && profile.responses.length > 0;

	// Navigate to Natural Matchmaker
	const handleStartSession = () => {
		navigate('/natural-matchmaker');
	};

	return (
		<div>
			{!profile ? (
				<div className="text-center py-8">
					<div className="rounded-full bg-blue-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
						<Mic className="text-blue-600" size={32} />
					</div>
					<h3 className="text-xl font-semibold text-gray-800 mb-2">Welcome to Your Dashboard</h3>
					<p className="text-gray-600 mb-6">
						Start your journey to finding the perfect therapist with our AI-powered voice matching.
					</p>
					<button
						onClick={handleStartSession}
						className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
					>
						<Play size={20} className="mr-2" />
						Start Your First Session
					</button>
				</div>
			) : (
				<div>
					{/* User Info Card */}
					<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
						<div className="flex items-center mb-4">
							<div className="bg-blue-100 rounded-full p-3">
								<User className="text-blue-600" size={24} />
							</div>
							<div className="ml-4">
								<h2 className="text-xl font-semibold text-gray-800">
									{profile.firstName} {profile.lastName}
								</h2>
								<p className="text-gray-600">{profile.email}</p>
							</div>
						</div>
						
						<div className="border-t pt-4 mt-4">
							<p className="text-sm text-gray-500">Profile created on</p>
							<p className="font-medium">{formatDate(profile.createdAt)}</p>
						</div>
						
						{profile.updatedAt && profile.updatedAt !== profile.createdAt && (
							<div className="pt-2">
								<p className="text-sm text-gray-500">Last updated</p>
								<p className="font-medium">{formatDate(profile.updatedAt)}</p>
							</div>
						)}
					</div>

					{/* Session Activity Card */}
					<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
						<h2 className="text-xl font-semibold text-gray-800 mb-4">Your Matchmaker Journey</h2>
						
						{hasSessionData ? (
							<div>
								<div className="mb-4">
									<p className="text-gray-600">
										You've completed <span className="font-medium">{profile.responses.length}</span> Natural Matchmaker sessions.
									</p>
								</div>
								
								<div className="flex items-center text-green-600 mb-4">
									<svg 
										xmlns="http://www.w3.org/2000/svg" 
										className="h-5 w-5 mr-2" 
										viewBox="0 0 20 20" 
										fill="currentColor"
									>
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
									</svg>
									<span>Sessions completed successfully</span>
								</div>

								{/* Start Another Session Button */}
								<button
									onClick={handleStartSession}
									className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
								>
									<Play size={16} className="mr-2" />
									Start Another Session
								</button>
							</div>
						) : (
							<div>
								<p className="text-gray-600 mb-4">
									You haven't started any voice matching sessions yet. Begin your journey to find the perfect therapist.
								</p>
								
								<div className="flex items-center text-gray-500 mb-4">
									<svg 
										xmlns="http://www.w3.org/2000/svg" 
										className="h-5 w-5 mr-2" 
										viewBox="0 0 20 20" 
										fill="currentColor"
									>
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
									</svg>
									<span>Ready to start your first session</span>
								</div>
								
								<button
									onClick={handleStartSession}
									className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
								>
									<Play size={16} className="mr-2" />
									Start Your First Session
								</button>
							</div>
						)}
					</div>
					
					{/* Analysis Results - Only show if user has session data */}
					{hasSessionData && profile.analysisResults && profile.analysisResults.tags && (
						<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
							<h2 className="text-xl font-semibold text-gray-800 mb-4">Your Personality Insights</h2>
							
							<div className="flex flex-wrap gap-2 mb-4">
								{profile.analysisResults.tags.map((tag, index) => (
									<span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
										{tag}
									</span>
								))}
							</div>
							
							{profile.analysisResults.summary && (
								<div className="mt-4 p-4 bg-gray-50 rounded-md">
									<p className="text-gray-700">{profile.analysisResults.summary}</p>
								</div>
							)}
						</div>
					)}
					
					{/* Navigation links to other sections - Only show if user has session data */}
					{hasSessionData && (
						<div className="mt-6 flex flex-wrap gap-3">
							<Link
								to="/client-profile/responses"
								className="text-blue-600 hover:underline flex items-center"
							>
								<MessageSquare size={16} className="mr-1" />
								<span>View session responses</span>
							</Link>
							
							{profile.personalityProfile && (
								<Link
									to="/client-profile/personality"
									className="text-blue-600 hover:underline flex items-center"
								>
									<BarChart size={16} className="mr-1" />
									<span>View personality profile</span>
								</Link>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default ClientProfile;